import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { makeStyles, fade } from '@material-ui/core/styles'
import CheckIcon from '@material-ui/icons/Check'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'

import {
  fetchScopedCentres,
  fetchChecklistHistory,
  getChecklistRunDetail,
  reviewChecklistPhotoFlag
} from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const CADENCES = [
  { value: 'morning', label: 'Morning', period: 'days' },
  { value: 'end_of_day', label: 'End of day', period: 'days' },
  { value: 'weekly', label: 'Weekly', period: 'weeks' },
  { value: 'monthly', label: 'Monthly', period: 'months' }
]
const cadenceMeta = (v) => CADENCES.find((c) => c.value === v) || CADENCES[0]

// What a photo-audit flag means, in words a person can act on.
const flagLabel = (flag, status, detail) => {
  const d = detail || {}
  let what
  if (flag === 'repeat') {
    what = d.kind === 'same_run'
      ? `Same file as the "${d.matched_text || 'another checkpoint'}" photo in this run`
      : `Same file as the "${d.matched_text || 'another'}" photo from ${d.matched_date || 'an earlier day'}`
  } else if (flag === 'stale') {
    what = `Photo file dated ${d.taken || 'before the due day'}, not taken on the day`
  } else if (flag === 'subject') {
    const seen = d.vision && d.vision.seen ? ` — looks like: ${d.vision.seen}` : ''
    what = `May not show this checkpoint${seen}`
  } else {
    what = 'Flagged by the photo audit'
  }
  const suffix = status === 'confirmed' ? ' · confirmed, run not counted'
    : status === 'cleared' ? ' · cleared' : ' · HQ to check'
  return what + suffix
}

const extractRows = (data) =>
  (data && (data.results || data.data)) || (Array.isArray(data) ? data : [])

const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
const todayStr = () => ymd(new Date())
const daysAgoStr = (n) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return ymd(d)
}

const fmtCol = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' })
  } catch (e) {
    return iso
  }
}

const fmtFull = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString([], {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    })
  } catch (e) {
    return iso
  }
}

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString([], {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'
    })
  } catch (e) {
    return ''
  }
}

// Whether a cadence is due on a given date — mirrors board._period_due_date
// (daily = every day, weekly = Tuesday, monthly = the 5th). Display-only:
// used to decide which past calendar days without a run read as "missed".
const isDueOn = (cadence, d) => {
  if (cadence === 'weekly') return d.getDay() === 2
  if (cadence === 'monthly') return d.getDate() === 5
  return true
}

// Six Monday-started weeks covering the given month (some cells fall outside it).
const monthCells = (year, monthIdx) => {
  const first = new Date(year, monthIdx, 1)
  const start = new Date(first)
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7))
  const cells = []
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    cells.push(d)
  }
  // Drop a trailing all-outside week so most months render 5 rows.
  while (cells.length > 35 && cells[cells.length - 7].getMonth() !== monthIdx) {
    cells.splice(cells.length - 7, 7)
  }
  return cells
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const useStyles = makeStyles((theme) => {
  const accent = theme.palette.primary.main
  const done = theme.palette.text.success || '#3f9d5b'
  const crit = theme.palette.error.main
  const surface = theme.palette.background.default
  const isDark = theme.palette.type === 'dark'
  const stripe = isDark ? 'rgba(255,255,255,0.04)' : '#f7f6f4'
  const surface2 = isDark ? 'rgba(255,255,255,0.06)' : '#f1efec'
  const line = theme.palette.divider
  const muted = fade(theme.palette.text.primary, 0.55)
  const faint = fade(theme.palette.text.primary, 0.38)

  return {
    filters: {
      display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', alignItems: 'center',
      marginBottom: theme.spacing(2)
    },
    field: { minWidth: 140 },
    date: { width: 150 },

    viewToggle: {
      display: 'inline-flex', border: `1px solid ${line}`, borderRadius: 99,
      overflow: 'hidden', marginLeft: 'auto'
    },
    viewBtn: {
      border: 'none', background: 'transparent', font: 'inherit', fontSize: 13,
      padding: theme.spacing(0.5, 1.75), cursor: 'pointer', color: muted
    },
    viewBtnActive: { background: fade(accent, 0.12), color: accent, fontWeight: 600 },

    summary: {
      background: surface, border: `1px solid ${line}`, borderRadius: 14,
      padding: theme.spacing(1.75, 2), marginBottom: theme.spacing(2)
    },
    summaryLine: { fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' },
    summaryLine2: { fontWeight: 700, color: accent },
    summarySub: { fontSize: 13, color: muted, marginTop: 4 },

    // ---- calendar ----
    calHeader: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: theme.spacing(1), marginBottom: theme.spacing(1)
    },
    calMonth: { fontSize: 15, fontWeight: 600, minWidth: 150, textAlign: 'center' },
    calWeekdays: {
      display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6,
      marginBottom: 4
    },
    calWeekday: { textAlign: 'center', fontSize: 11, fontWeight: 600, color: faint },
    calGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
    dayCell: {
      minHeight: 58, borderRadius: 10, border: `1px solid ${line}`,
      padding: theme.spacing(0.5, 0.75), display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', background: surface,
      cursor: 'pointer', '&:hover': { borderColor: accent }
    },
    dayOutside: { visibility: 'hidden' },
    dayFuture: { '& $dayNum': { color: faint } },
    dayNum: { fontSize: 12, fontWeight: 600, color: muted },
    // round dots = morning / end-of-day; W / M letter chip = weekly / monthly
    dots: { display: 'flex', gap: 3, alignItems: 'center', minHeight: 14 },
    dot: { width: 9, height: 9, borderRadius: '50%', flex: '0 0 auto' },
    dotDone: { background: done },
    dotMiss: { background: crit },
    dotDue: { background: 'transparent', border: `1.5px solid #b57907` },
    dotUp: { background: 'transparent', border: `1.5px solid ${line}` },
    // submitted, but Gordon's photo audit disqualified it — it does not count
    dotFlag: { background: '#b57907' },
    // the chip announces the upcoming duty (outlined, Alfred orange) and then
    // fills green / red with the outcome — full strength even on faded days.
    chip: {
      minWidth: 14, height: 14, borderRadius: 4, padding: '0 2px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 800, lineHeight: 1, flex: '0 0 auto'
    },
    chipUp: { color: accent, border: `1.5px solid ${accent}`, background: 'transparent' },
    chipDue: { color: '#b57907', border: `1.5px solid #b57907`, background: 'transparent' },
    chipDone: { color: '#fff', background: done },
    chipMiss: { color: '#fff', background: crit },
    chipFlag: { color: '#fff', background: '#b57907' },
    calLegend: {
      display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap',
      marginTop: theme.spacing(1.5), fontSize: 12, color: muted,
      alignItems: 'center'
    },
    legendDot: {
      display: 'inline-block', verticalAlign: -1, marginRight: 5
    },
    // day-detail sheet (per-cadence rows, tap a row → full run)
    dayRow: {
      display: 'flex', alignItems: 'center', gap: theme.spacing(1),
      padding: theme.spacing(1, 0.5), borderBottom: `1px solid ${fade(line, 0.6)}`,
      width: '100%', background: 'transparent', border: 'none', font: 'inherit',
      color: 'inherit', textAlign: 'left', appearance: 'none'
    },
    dayRowTappable: { cursor: 'pointer', '&:hover': { background: stripe } },
    dayRowMark: { width: 20, textAlign: 'center', fontWeight: 700, flex: '0 0 auto' },
    markDone: { color: done },
    markMiss: { color: crit },
    markDue: { color: '#b57907' },
    markFlag: { color: '#b57907' },
    markNa: { color: faint },
    dayRowName: { flex: 1, fontSize: 13.5 },
    dayRowMeta: { fontSize: 12, color: muted, fontVariantNumeric: 'tabular-nums' },

    // ---- run viewer dialog ----
    runArea: {
      fontSize: 12, fontWeight: 600, color: faint, textTransform: 'none',
      margin: theme.spacing(1.5, 0, 0.5)
    },
    runItem: {
      display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1),
      padding: theme.spacing(0.75, 0), borderBottom: `1px solid ${fade(line, 0.6)}`
    },
    runTick: { color: done, fontSize: 18, marginTop: 1 },
    runMissTick: { color: faint, fontSize: 14, width: 18, textAlign: 'center', marginTop: 2 },
    runText: { fontSize: 13.5, flex: 1 },
    runTextMissed: { color: muted },
    runValue: { fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    runNote: { fontSize: 12, color: muted, marginTop: 2 },
    runNo: {
      color: crit, fontSize: 11, fontWeight: 800, width: 18, textAlign: 'center', marginTop: 3
    },
    runNoteProblem: { color: crit, fontWeight: 600 },
    runThumb: {
      width: 44, height: 44, objectFit: 'cover', borderRadius: 8,
      border: `1px solid ${line}`, flex: '0 0 auto'
    },
    runMeta: { fontSize: 12.5, color: muted, marginTop: theme.spacing(1.5) },

    scroll: { overflowX: 'auto', border: `1px solid ${line}`, borderRadius: 12, background: surface },
    table: { borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' },
    th: {
      padding: theme.spacing(1, 0.75), fontSize: 11.5, fontWeight: 600, color: muted,
      textAlign: 'center', borderBottom: `1px solid ${line}`, whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums'
    },
    thStatus: { display: 'block', fontSize: 10.5, fontWeight: 600, marginTop: 2 },
    cornerTh: {
      position: 'sticky', left: 0, zIndex: 3, background: surface2,
      textAlign: 'left', minWidth: 200, maxWidth: 250,
      padding: theme.spacing(1, 1.25), borderBottom: `1px solid ${line}`, borderRight: `1px solid ${line}`,
      fontWeight: 600
    },
    areaCell: {
      position: 'sticky', left: 0, zIndex: 2, background: surface2, textAlign: 'left',
      fontSize: 12, color: faint, fontWeight: 600,
      padding: theme.spacing(0.75, 1.25), borderBottom: `1px solid ${line}`, borderRight: `1px solid ${line}`
    },
    areaFill: { background: surface2, borderBottom: `1px solid ${line}` },
    rowLabel: {
      position: 'sticky', left: 0, zIndex: 1, background: surface,
      padding: theme.spacing(0.75, 1.25), fontSize: 13, borderBottom: `1px solid ${line}`,
      borderRight: `1px solid ${line}`, minWidth: 200, maxWidth: 250
    },
    rowLabelStripe: { background: stripe },
    cell: {
      padding: theme.spacing(0.5), textAlign: 'center', borderBottom: `1px solid ${line}`,
      minWidth: 46, fontSize: 12
    },
    cellStripe: { background: stripe },
    tick: { color: done, fontSize: 17, verticalAlign: 'middle' },
    open: { color: faint, fontSize: 13 },
    // a checkpoint the centre answered No — the note is on hover
    no: { color: crit, fontSize: 11.5, fontWeight: 800, cursor: 'help' },
    dataVal: { fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 11.5 },
    camera: { color: done, fontSize: 12, marginLeft: 1, verticalAlign: 'middle' },
    // photo-audit flags: amber while HQ has yet to rule, red once confirmed
    flagOpen: { color: '#b57907', fontSize: 12, marginLeft: 2, fontWeight: 700, cursor: 'help' },
    flagConfirmed: { color: crit, fontSize: 12, marginLeft: 2, fontWeight: 700, cursor: 'help' },
    thFlag: { color: crit, marginLeft: 4, cursor: 'help' },
    runFlag: {
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
      fontSize: 12, marginTop: 4, color: '#8a5a05'
    },
    runFlagConfirmed: { color: crit },
    runFlagCleared: { color: faint, textDecoration: 'line-through' },
    runFlagBtn: { fontSize: 11, padding: '0 8px', minWidth: 0, lineHeight: '20px' },

    // all-centres overview
    allHead: { fontSize: 13, color: muted, marginBottom: theme.spacing(1.5) },
    centreRow: {
      display: 'flex', alignItems: 'center', gap: theme.spacing(1.5),
      background: surface, border: `1px solid ${line}`, borderRadius: 12,
      padding: theme.spacing(1.25, 1.5), marginBottom: theme.spacing(1), cursor: 'pointer', width: '100%',
      textAlign: 'left', font: 'inherit', color: 'inherit', appearance: 'none'
    },
    centreName: { flex: '1 1 auto', fontWeight: 600, fontSize: 14, minWidth: 0 },
    centreDays: { fontSize: 12.5, color: muted, fontVariantNumeric: 'tabular-nums', minWidth: 92, textAlign: 'right' },
    barWrap: { width: 120, height: 8, borderRadius: 99, background: line, overflow: 'hidden', flex: '0 0 auto' },
    barFill: { height: '100%', borderRadius: 99 },
    barPct: { minWidth: 42, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 13 },

    empty: { textAlign: 'center', color: muted, padding: theme.spacing(6, 2) },
    loadingWrap: { textAlign: 'center', marginTop: theme.spacing(4) },
    statusDone: { color: done },
    statusPending: { color: faint },
    statusOverdue: { color: crit }
  }
})

// Opens on the CALENDAR of `initialCentre` (the centre selected on the board
// tab) — the all-centres roll-up stays reachable via the centre dropdown.
const ChecklistHistory = ({ initialCentre }) => {
  const classes = useStyles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  const [centreUuid, setCentreUuid] = useState(initialCentre || '')
  const [cadence, setCadence] = useState('morning')
  const [view, setView] = useState('calendar')
  const [month, setMonth] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [from, setFrom] = useState(daysAgoStr(21))
  const [to, setTo] = useState(todayStr())
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const [runOpen, setRunOpen] = useState(false)
  const [run, setRun] = useState(null)
  const [runLoading, setRunLoading] = useState(false)
  // {iso, cadences} — the tapped day's per-cadence sheet
  const [dayDetail, setDayDetail] = useState(null)
  // run-item uuid whose photo flag is being confirmed/cleared right now
  const [reviewing, setReviewing] = useState(null)
  // bumped after HQ rules on a flag so the grid/calendar refetch
  const [reloadKey, setReloadKey] = useState(0)

  // HQ rules on a photo flag from the run viewer; the item updates in place
  // and the grid/calendar refetch so "not counted" shows straight away.
  const reviewFlag = useCallback(async (ri, action) => {
    setReviewing(ri.uuid)
    try {
      const updated = await reviewChecklistPhotoFlag(ri.uuid, action)
      setRun((prev) => prev && prev.run_items
        ? { ...prev, run_items: prev.run_items.map((x) => (x.uuid === ri.uuid ? { ...x, ...updated } : x)) }
        : prev)
      showToast(action === 'confirm' ? 'Flag confirmed — this run no longer counts' : 'Flag cleared', constants.SUCCESS)
      setReloadKey((k) => k + 1)
    } catch (error) {
      const data = error && error.response && error.response.data
      showToast((data && data.detail) || 'Could not update the flag', constants.ERROR)
    } finally {
      setReviewing(null)
    }
  }, [])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const res = await fetchScopedCentres()
        const rows = extractRows(res)
        if (!active) return
        setCentres(rows)
        setUnrestricted(Boolean(res?.unrestricted))
        // No centre handed in (board hadn't loaded yet) — default to the first
        // centre so History always opens on a calendar, not the roll-up.
        setCentreUuid((prev) => prev || (rows[0] ? rows[0].uuid : 'all'))
      } catch (error) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => { active = false }
  }, [])

  // Calendar view fetches the visible month; the grid keeps its free range.
  const useCalendar = view === 'calendar' && centreUuid !== 'all'
  const monthFrom = ymd(month)
  const monthTo = ymd(new Date(month.getFullYear(), month.getMonth() + 1, 0))

  const load = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      const res = await fetchChecklistHistory({
        centre: centreUuid,
        // The calendar shows every cadence at once (dots); the grid and the
        // all-centres roll-up stay per-cadence.
        cadence: useCalendar ? 'all' : cadence,
        from: useCalendar ? monthFrom : from,
        to: useCalendar ? monthTo : to
      })
      setData(res && res.data && res.mode === undefined ? res.data : res)
    } catch (error) {
      showToast('Could not load checklist history', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [centreUuid, cadence, from, to, useCalendar, monthFrom, monthTo, reloadKey])

  useEffect(() => { load() }, [load])

  const openRun = useCallback(async (runUuid) => {
    setRunOpen(true)
    setRunLoading(true)
    try {
      const detail = await getChecklistRunDetail(runUuid)
      setRun(detail)
    } catch (error) {
      showToast('Could not load that day’s checklist', constants.ERROR)
      setRunOpen(false)
    } finally {
      setRunLoading(false)
    }
  }, [])

  const closeRun = useCallback(() => {
    setRunOpen(false)
    setRun(null)
  }, [])

  const mode = (data && data.mode) || 'centre'
  const meta = cadenceMeta(cadence)

  const barColor = (pct) => {
    const done = '#3f9d5b'
    if (pct >= 80) return done
    if (pct >= 40) return '#c8791a'
    return '#cf3b3b'
  }

  // ---- all-centres overview ----
  const renderAll = () => {
    const rows = (data && data.centres) || []
    if (!rows.length) {
      return <Typography className={classes.empty}>No checklist runs recorded for any centre in this range.</Typography>
    }
    return (
      <>
        <Typography className={classes.allHead}>
          How thoroughly each centre completed its {meta.label.toLowerCase()} checklist — tap a centre for the day-by-day detail.
        </Typography>
        {rows.map((c) => (
          <Box
            component="button"
            key={c.uuid}
            className={classes.centreRow}
            onClick={() => setCentreUuid(c.uuid)}
          >
            <span className={classes.centreName}>{c.name}</span>
            <span className={classes.centreDays}>{c.completed}/{c.runs} {meta.period} done</span>
            <span className={classes.barWrap}>
              <span className={classes.barFill} style={{ width: `${c.avg_completion}%`, background: barColor(c.avg_completion) }} />
            </span>
            <span className={classes.barPct}>{c.avg_completion}%</span>
          </Box>
        ))}
      </>
    )
  }

  // ---- single-centre data ----
  const columns = (data && data.columns) || []
  const checkpoints = (data && data.checkpoints) || []
  const cells = (data && data.cells) || {}
  const rollup = (data && data.rollup) || {}
  const centreName = (data && data.centre && data.centre.name) || 'This centre'

  const flatRows = useMemo(() => {
    const order = []
    const map = {}
    checkpoints.forEach((cp) => {
      if (!map[cp.area]) { map[cp.area] = []; order.push(cp.area) }
      map[cp.area].push(cp)
    })
    const out = []
    let i = 0
    order.forEach((area) => {
      out.push({ type: 'area', area })
      map[area].forEach((cp) => { out.push({ type: 'cp', cp, stripe: i % 2 === 1 }); i += 1 })
    })
    return out
  }, [checkpoints])

  const statusClass = (status) =>
    status === 'completed' ? classes.statusDone
      : status === 'overdue' ? classes.statusOverdue : classes.statusPending

  const renderGridCell = (cp, col) => {
    const cell = cells[cp.key] && cells[cp.key][col.due_date]
    if (!cell) return <span className={classes.open}>·</span>
    if (!cell.checked) return <span className={classes.open}>○</span>
    if (cell.answer === 'no') {
      return (
        <span>
          <span className={classes.no} title={cell.note ? `No — ${cell.note}` : 'No'}>No</span>
          {cell.photo_url ? <PhotoCameraIcon className={classes.camera} /> : null}
        </span>
      )
    }
    if (cp.capture_type === 'data' && cell.value) return <span className={classes.dataVal}>{cell.value}</span>
    return (
      <span>
        <CheckIcon className={classes.tick} />
        {cell.photo_url ? <PhotoCameraIcon className={classes.camera} /> : null}
        {cell.photo_flag ? (
          <span
            className={cell.photo_flag_status === 'confirmed' ? classes.flagConfirmed : classes.flagOpen}
            title={flagLabel(cell.photo_flag, cell.photo_flag_status, cell.photo_flag_detail)}
          >
            ⚠
          </span>
        ) : null}
      </span>
    )
  }

  // ---- calendar (all cadences as dots per day) ----
  const days = (data && data.days) || {}

  // One dot per cadence per day: round dots = morning / end-of-day (due every
  // day), square dots = weekly (Tue) / monthly (5th) shown only when due.
  // done=filled green, missed=filled red, due today=amber outline,
  // upcoming=grey outline.
  const dotStates = (iso, d, today) => {
    const info = days[iso] || {}
    const past = iso < today
    const isToday = iso === today
    return CADENCES.map(({ value }) => {
      const big = value === 'weekly' || value === 'monthly'
      const entry = info[value]
      // Weekly/monthly dots appear on their due day — or wherever a run
      // actually exists (historic runs may carry an older due-date rule).
      if (big && !entry && !isDueOn(value, d)) return null
      let state
      if (entry && entry.status === 'completed' && entry.counted === false) state = 'flagged'
      else if (entry && entry.status === 'completed') state = 'done'
      else if (past) state = 'miss'
      else if (isToday) state = 'due'
      else state = 'up'
      return { cadence: value, big, state, entry }
    }).filter(Boolean)
  }

  const dotClass = (s) => (
    s === 'done' ? classes.dotDone
      : s === 'flagged' ? classes.dotFlag
        : s === 'miss' ? classes.dotMiss
          : s === 'due' ? classes.dotDue : classes.dotUp
  )

  const chipClass = (s) => (
    s === 'done' ? classes.chipDone
      : s === 'flagged' ? classes.chipFlag
        : s === 'miss' ? classes.chipMiss
        : s === 'due' ? classes.chipDue : classes.chipUp
  )

  const renderCalendar = () => {
    const today = todayStr()
    const cellsForMonth = monthCells(month.getFullYear(), month.getMonth())
    const monthLabel = month.toLocaleDateString([], { month: 'long', year: 'numeric' })

    return (
      <>
        <Box className={classes.summary}>
          <Typography className={classes.summaryLine}>
            {centreName} submitted{' '}
            <span className={classes.summaryLine2}>{rollup.completed || 0} of {rollup.runs || 0} checklists</span> this month.
          </Typography>
          <Typography className={classes.summarySub}>
            tap a day to see each checklist, then a checklist for its full detail
          </Typography>
        </Box>

        <Box className={classes.calHeader}>
          <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography className={classes.calMonth}>{monthLabel}</Typography>
          <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box className={classes.calWeekdays}>
          {WEEKDAYS.map((w) => <span key={w} className={classes.calWeekday}>{w}</span>)}
        </Box>
        <Box className={classes.calGrid}>
          {cellsForMonth.map((d) => {
            const iso = ymd(d)
            const outside = d.getMonth() !== month.getMonth()
            const future = iso > today
            const dots = dotStates(iso, d, today)
            return (
              <Box
                key={iso}
                className={[
                  classes.dayCell,
                  outside ? classes.dayOutside : '',
                  future ? classes.dayFuture : ''
                ].join(' ')}
                onClick={() => setDayDetail({ iso, date: d })}
              >
                <span className={classes.dayNum}>{d.getDate()}</span>
                <span className={classes.dots}>
                  {dots.map((dot) => (
                    dot.big ? (
                      <span key={dot.cadence} className={`${classes.chip} ${chipClass(dot.state)}`}>
                        {dot.cadence === 'weekly' ? 'W' : 'M'}
                      </span>
                    ) : (
                      <span key={dot.cadence} className={`${classes.dot} ${dotClass(dot.state)}`} />
                    )
                  ))}
                </span>
              </Box>
            )
          })}
        </Box>

        <Box className={classes.calLegend}>
          <span><span className={`${classes.dot} ${classes.dotDone} ${classes.legendDot}`} />submitted</span>
          <span><span className={`${classes.dot} ${classes.dotMiss} ${classes.legendDot}`} />missed</span>
          <span><span className={`${classes.dot} ${classes.dotFlag} ${classes.legendDot}`} />submitted, not counted</span>
          <span><span className={`${classes.dot} ${classes.dotDue} ${classes.legendDot}`} />due today</span>
          <span><span className={`${classes.chip} ${classes.chipUp} ${classes.legendDot}`}>W</span>weekly coming up</span>
          <span><span className={`${classes.chip} ${classes.chipDone} ${classes.legendDot}`}>M</span>monthly done</span>
        </Box>
      </>
    )
  }

  // ---- read-only run viewer (calendar click-through) ----
  const runItemsByArea = useMemo(() => {
    if (!run || !run.run_items) return []
    const order = []
    const map = {}
    run.run_items.forEach((ri) => {
      const area = (ri.item && ri.item.area) || 'Other'
      if (!map[area]) { map[area] = []; order.push(area) }
      map[area].push(ri)
    })
    return order.map((area) => ({ area, items: map[area] }))
  }, [run])

  const renderRunDialog = () => (
    <Dialog open={runOpen} onClose={closeRun} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>
        {run && run.template ? run.template.name : 'Checklist'}
        <Typography component="span" style={{ display: 'block', fontSize: 13, opacity: 0.7 }}>
          {centreName}{run && run.due_date ? ` · ${fmtFull(run.due_date)}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {runLoading && <Box className={classes.loadingWrap}><CircularProgress size={28} /></Box>}
        {!runLoading && run && runItemsByArea.map(({ area, items }) => (
          <Box key={area}>
            <Typography className={classes.runArea}>{area}</Typography>
            {items.map((ri) => (
              <Box key={ri.uuid} className={classes.runItem}>
                {ri.answer === 'no'
                  ? <span className={classes.runNo}>No</span>
                  : ri.checked
                    ? <CheckIcon className={classes.runTick} />
                    : <span className={classes.runMissTick}>○</span>}
                <Box className={`${classes.runText}${ri.checked ? '' : ` ${classes.runTextMissed}`}`}>
                  {ri.item ? ri.item.text : ''}
                  {ri.note ? (
                    <Typography className={`${classes.runNote}${ri.answer === 'no' ? ` ${classes.runNoteProblem}` : ''}`}>
                      {ri.note}
                    </Typography>
                  ) : null}
                  {ri.photo_flag ? (
                    <Box className={`${classes.runFlag}${ri.photo_flag_status === 'confirmed' ? ` ${classes.runFlagConfirmed}` : ''}${ri.photo_flag_status === 'cleared' ? ` ${classes.runFlagCleared}` : ''}`}>
                      <span>⚠ {flagLabel(ri.photo_flag, ri.photo_flag_status, ri.photo_flag_detail)}</span>
                      {unrestricted && ri.photo_flag_status === 'open' ? (
                        <>
                          <Button size="small" variant="outlined" className={classes.runFlagBtn} disabled={reviewing === ri.uuid} onClick={() => reviewFlag(ri, 'confirm')}>Confirm</Button>
                          <Button size="small" variant="outlined" className={classes.runFlagBtn} disabled={reviewing === ri.uuid} onClick={() => reviewFlag(ri, 'clear')}>Clear</Button>
                        </>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
                {ri.value ? <span className={classes.runValue}>{ri.value}</span> : null}
                {ri.photo && ri.photo.url ? (
                  <a href={ri.photo.url} target="_blank" rel="noopener noreferrer">
                    <img src={ri.photo.url} alt="evidence" className={classes.runThumb} />
                  </a>
                ) : null}
              </Box>
            ))}
          </Box>
        ))}
        {!runLoading && run && (
          <Typography className={classes.runMeta}>
            {run.status === 'completed'
              ? `Submitted${run.completed_by && run.completed_by.name ? ` by ${run.completed_by.name}` : ''}${run.completed_at ? ` · ${fmtTime(run.completed_at)}` : ''}`
              : 'Not submitted yet'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeRun} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  )

  // ---- day sheet: the tapped day's four cadences ----
  const dayRows = useMemo(() => {
    if (!dayDetail) return []
    const info = days[dayDetail.iso] || {}
    const today = todayStr()
    const past = dayDetail.iso < today
    const isToday = dayDetail.iso === today
    return CADENCES.map(({ value, label }) => {
      const big = value === 'weekly' || value === 'monthly'
      const entry = info[value]
      let state
      if (big && !entry && !isDueOn(value, dayDetail.date)) state = 'na'
      else if (entry && entry.status === 'completed' && entry.counted === false) state = 'flagged'
      else if (entry && entry.status === 'completed') state = 'done'
      else if (past) state = 'miss'
      else if (isToday) state = 'due'
      else state = 'up'
      return { cadence: value, label, state, entry }
    })
  }, [dayDetail, days])

  const rowMark = (state) => (
    state === 'done' ? <span className={`${classes.dayRowMark} ${classes.markDone}`}>✓</span>
      : state === 'flagged' ? <span className={`${classes.dayRowMark} ${classes.markFlag}`}>⚠</span>
      : state === 'miss' ? <span className={`${classes.dayRowMark} ${classes.markMiss}`}>✗</span>
        : state === 'due' ? <span className={`${classes.dayRowMark} ${classes.markDue}`}>●</span>
          : <span className={`${classes.dayRowMark} ${classes.markNa}`}>—</span>
  )

  const problemsText = (entry) => {
    const n = (entry && entry.problems) || 0
    return n ? ` · ${n} problem${n === 1 ? '' : 's'} reported` : ''
  }

  const rowMeta = (r) => {
    if (r.state === 'done') return `${r.entry.done}/${r.entry.total} · submitted${problemsText(r.entry)}`
    if (r.state === 'flagged') return `${r.entry.done}/${r.entry.total} · submitted, not counted (${r.entry.flags} flagged)${problemsText(r.entry)}`
    if (r.state === 'miss') return r.entry ? `${r.entry.done}/${r.entry.total} · not submitted` : 'missed'
    if (r.state === 'due') return 'due today'
    if (r.state === 'up') return 'upcoming'
    return 'not due'
  }

  const renderDayDialog = () => (
    <Dialog
      open={Boolean(dayDetail)}
      onClose={() => setDayDetail(null)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{dayDetail ? fmtFull(dayDetail.iso) : ''}</DialogTitle>
      <DialogContent dividers>
        {dayRows.map((r) => {
          const tappable = Boolean(r.entry)
          return (
            <Box
              component={tappable ? 'button' : 'div'}
              key={r.cadence}
              className={`${classes.dayRow}${tappable ? ` ${classes.dayRowTappable}` : ''}`}
              onClick={tappable ? () => { setDayDetail(null); openRun(r.entry.run_uuid) } : undefined}
            >
              {rowMark(r.state)}
              <span className={classes.dayRowName}>{r.label}</span>
              <span className={classes.dayRowMeta}>{rowMeta(r)}</span>
            </Box>
          )
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDayDetail(null)} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  )

  // ---- single-centre grid ----
  const hasGrid = columns.length > 0 && checkpoints.length > 0

  const renderGrid = () => {
    if (!hasGrid) {
      return <Typography className={classes.empty}>No checklist history for this centre and range.</Typography>
    }
    return (
      <>
        <Box className={classes.summary}>
          <Typography className={classes.summaryLine}>
            {centreName} completed the {meta.label.toLowerCase()} checklist on{' '}
            <span className={classes.summaryLine2}>{rollup.completed || 0} of {rollup.runs || 0} {meta.period}</span>.
          </Typography>
          <Typography className={classes.summarySub}>
            {rollup.avg_completion != null ? `${rollup.avg_completion}% of checks done on average` : ''}
            {` · ${checkpoints.length} checkpoints each ${meta.period === 'days' ? 'day' : meta.period.slice(0, -1)}`}
          </Typography>
        </Box>

        <Box className={classes.scroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={`${classes.th} ${classes.cornerTh}`}>Checkpoint</th>
                {columns.map((col) => (
                  <th key={col.due_date} className={classes.th}>
                    {fmtCol(col.due_date)}
                    <span className={`${classes.thStatus} ${statusClass(col.status)}`}>
                      {col.done}/{col.total}
                      {col.status === 'completed' && col.counted === false ? (
                        <span className={classes.thFlag} title={`Submitted but not counted — ${col.flags} flagged photo${col.flags === 1 ? '' : 's'}`}>⚠</span>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {flatRows.map((row, idx) => (
                row.type === 'area' ? (
                  <tr key={`a-${row.area}`}>
                    <td className={classes.areaCell}>{row.area}</td>
                    {columns.map((col) => <td key={col.due_date} className={classes.areaFill} />)}
                  </tr>
                ) : (
                  <tr key={row.cp.key}>
                    <td className={`${classes.rowLabel}${row.stripe ? ` ${classes.rowLabelStripe}` : ''}`} title={row.cp.text}>
                      {row.cp.text}
                    </td>
                    {columns.map((col) => (
                      <td key={col.due_date} className={`${classes.cell}${row.stripe ? ` ${classes.cellStripe}` : ''}`}>
                        {renderGridCell(row.cp, col)}
                      </td>
                    ))}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </Box>
      </>
    )
  }

  return (
    <Box>
      <Box className={classes.filters}>
        <FormControl size="small">
          <Select className={classes.field} value={centreUuid} onChange={(e) => setCentreUuid(e.target.value)}>
            {unrestricted && <MenuItem value="all">All centres</MenuItem>}
            {centres.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        {!useCalendar && (
          <FormControl size="small">
            <Select className={classes.field} value={cadence} onChange={(e) => setCadence(e.target.value)}>
              {CADENCES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {!useCalendar && (
          <>
            <TextField
              className={classes.date} size="small" type="date" label="From"
              value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.date} size="small" type="date" label="To"
              value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
            />
          </>
        )}
        {centreUuid !== 'all' && (
          <Box className={classes.viewToggle}>
            <button
              type="button"
              className={`${classes.viewBtn}${view === 'calendar' ? ` ${classes.viewBtnActive}` : ''}`}
              onClick={() => setView('calendar')}
            >
              Calendar
            </button>
            <button
              type="button"
              className={`${classes.viewBtn}${view === 'grid' ? ` ${classes.viewBtnActive}` : ''}`}
              onClick={() => setView('grid')}
            >
              Grid
            </button>
          </Box>
        )}
      </Box>

      {loading && <Box className={classes.loadingWrap}><CircularProgress /></Box>}
      {!loading && mode === 'all' && renderAll()}
      {!loading && mode === 'centre_cadences' && renderCalendar()}
      {!loading && mode === 'centre' && renderGrid()}
      {renderDayDialog()}
      {renderRunDialog()}
    </Box>
  )
}

export default ChecklistHistory
